import React from "react";
import { Card, CardContent, Typography, Box, Avatar } from "@mui/material";
import { TrendingUp, TrendingDown } from "@mui/icons-material";

/**
 * Reusable StatsCard component for displaying metrics
 * 
 * @param {string|number} value - The main value to display
 * @param {string} label - Label text
 * @param {ReactNode} icon - Icon component
 * @param {string} color - Color variant (primary, success, warning, error, info)
 * @param {number} trend - Trend value (positive/negative)
 * @param {string} trendLabel - Trend label
 * @param {Function} onClick - Click handler
 * @param {Object} sx - Custom styles
 */
const StatsCard = ({
  value,
  label,
  icon,
  color = "primary",
  trend,
  trendLabel,
  onClick,
  sx = {},
}) => {
  const colorMap = {
    primary: { bg: "primary.main", text: "white", light: "primary.light" },
    secondary: { bg: "secondary.main", text: "white", light: "secondary.light" },
    success: { bg: "success.main", text: "white", light: "success.light" },
    warning: { bg: "warning.main", text: "white", light: "warning.light" },
    error: { bg: "error.main", text: "white", light: "error.light" },
    info: { bg: "info.main", text: "white", light: "info.light" },
  };

  const colors = colorMap[color] || colorMap.primary;
  const isPositiveTrend = trend && trend > 0;

  return (
    <Card
      onClick={onClick}
      sx={{
        cursor: onClick ? "pointer" : "default",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": onClick
          ? {
            transform: "translateY(-4px)",
            boxShadow: 4,
          }
          : {},
        ...sx,
      }}
    >
      <CardContent
        sx={{
          textAlign: "center",
          py: 2,
          ...(color && color !== "default"
            ? {
              bgcolor: colors.bg,
              color: colors.text,
            }
            : {}),
        }}
      >
        {icon && (
          <Avatar
            sx={{
              bgcolor: colors.light,
              mx: "auto",
              mb: 1,
              width: 48,
              height: 48,
            }}
          >
            {icon}
          </Avatar>
        )}
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            color: color && color !== "default" ? colors.text : "inherit",
            mb: 0.5,
          }}
        >
          {value}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: color && color !== "default" ? colors.text : "text.secondary",
            opacity: 0.9,
          }}
        >
          {label}
        </Typography>
        {trend !== undefined && trend !== null && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
              mt: 1,
            }}
          >
            {isPositiveTrend ? (
              <TrendingUp sx={{ fontSize: 16 }} />
            ) : (
              <TrendingDown sx={{ fontSize: 16 }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: isPositiveTrend ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {Math.abs(trend)}% {trendLabel || ""}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default StatsCard;

